import { Modal } from "antd";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useProfile } from "@/context/ProfileContext";
import { addDoc, collection, Timestamp } from "firebase/firestore";
import { db } from "@/config/firebaseConfig";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";

interface BulkMessageModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedChats: string[];
  adId: string;
  onMessageSent: () => void;
  matches: any[];
}

export default function BulkMessageModal({
  open,
  onOpenChange,
  selectedChats,
  adId,
  onMessageSent,
  matches,
}: BulkMessageModalProps) {
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const { businessProfile } = useProfile();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const selectedRecipients = matches.filter((match) =>
    selectedChats.includes(match.uid)
  );

  const handleSendMessage = async () => {
    if (message.trim() === "") return;

    setLoading(true);
    try {
      await Promise.all(
        selectedChats.map((chatId) =>
          addDoc(collection(db, `Chats/${adId}/Messages`), {
            message: message,
            timestamp: Timestamp.now(),
            sender: "business",
            name: businessProfile?.business,
            sellerUid: chatId,
            readBySeller: false,
          })
        )
      );

      onOpenChange(false);

      queryClient.invalidateQueries({
        queryKey: ["chat-matches", adId],
      });
      toast({
        title: "Meddelanden skickade",
        description: `Ditt meddelande har skickats till ${selectedChats.length} mottagare.`,
      });
      onMessageSent();
      setMessage("");
    } catch (error) {
      console.error("Error sending bulk message:", error);
      toast({
        title: "Något gick fel",
        description: "Kunde inte skicka meddelandena.",
        variant: "destructive",
      });
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Skicka massmeddelande"
      open={open}
      onCancel={() => onOpenChange(false)}
      footer={
        <div className="flex justify-end space-x-2 p-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Avbryt
          </Button>
          <Button onClick={handleSendMessage} disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Skicka
          </Button>
        </div>
      }>
      <div className="p-4">
        <p>
          Skriv ett meddelande som ska skickas till de {selectedChats.length}{" "}
          valda mottagarna.
        </p>
        <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto p-2 border rounded-md my-4">
          {selectedRecipients.map((recipient) => (
            <Badge key={recipient.uid} variant="secondary">
              {recipient.givenName} {recipient.surname}
            </Badge>
          ))}
        </div>
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Skriv ditt meddelande här..."
        />
      </div>
    </Modal>
  );
}
